import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { IslandDefinition } from '../game/types';
import type { Assets } from './models';

const SEGMENTS = 64;
const RINGS = [0, .13, .29, .46, .63, .76, .86, .95, 1.025];
const HEIGHTS = [.7, .78, .69, .49, .28, .12, .038, .012, -.04];

function outline(angle: number, seed: number): number {
  return .965 + Math.sin(angle * 3 + seed) * .035 + Math.sin(angle * 5 + seed * .7) * .045 + Math.cos(angle * 9 + seed) * .018;
}

interface Terrain { geometry: THREE.BufferGeometry; heightAt: (x: number, z: number) => number }

/** Decoration samples the actual rendered triangles, so roots never hover over slopes. */
function terrain(island: IslandDefinition, seed: number): Terrain {
  const positions: number[] = [], colors: number[] = [], indices: number[] = [];
  const grass = new THREE.Color(0x2e663e), crown = new THREE.Color(0x47784b);
  const limestone = new THREE.Color(0xa1a78a), sand = new THREE.Color(0xe2c78e), wetSand = new THREE.Color(0xbba877);
  for (let ring = 0; ring < RINGS.length; ring++) for (let j = 0; j <= SEGMENTS; j++) {
    const angle = j / SEGMENTS * Math.PI * 2;
    const radius = island.radius * RINGS[ring] * outline(angle, seed);
    const relief = ring > 0 && ring < 6 ? Math.sin(angle * 3 + seed) * .055 + Math.sin(angle * 6 + seed * 2) * .016 : 0;
    const y = HEIGHTS[ring] * island.height + relief * island.height * Math.sin(RINGS[ring] * Math.PI);
    positions.push(Math.sin(angle) * radius, y, Math.cos(angle) * radius);
    const tint = ring >= 7 ? sand.clone().lerp(wetSand, ring === 8 ? .65 : .05) : grass.clone().lerp(crown, Math.max(0, 1 - RINGS[ring] * 1.6));
    if (ring < 5) tint.lerp(limestone, Math.max(0, Math.sin(angle * 2 + seed) - .62) * .32);
    const shade = .93 + Math.sin(angle * 7 + ring * 2 + seed) * .035 + Math.cos(angle * 13 + ring) * .025;
    colors.push(tint.r * shade, tint.g * shade, tint.b * shade);
    if (ring < RINGS.length - 1 && j < SEGMENTS) {
      const a = ring * (SEGMENTS + 1) + j;
      indices.push(a, a + SEGMENTS + 1, a + 1, a + 1, a + SEGMENTS + 1, a + SEGMENTS + 2);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices); geometry.computeVertexNormals();
  return { geometry, heightAt(x, z) {
    for (let i = 0; i < indices.length; i += 3) {
      const a = indices[i] * 3, b = indices[i + 1] * 3, c = indices[i + 2] * 3;
      const ax = positions[a], az = positions[a + 2], bx = positions[b], bz = positions[b + 2], cx = positions[c], cz = positions[c + 2];
      const denominator = (bz - cz) * (ax - cx) + (cx - bx) * (az - cz);
      if (Math.abs(denominator) < .00001) continue;
      const u = ((bz - cz) * (x - cx) + (cx - bx) * (z - cz)) / denominator;
      const v = ((cz - az) * (x - cx) + (ax - cx) * (z - cz)) / denominator;
      if (u >= -.00001 && v >= -.00001 && u + v <= 1.00001) return u * positions[a + 1] + v * positions[b + 1] + (1 - u - v) * positions[c + 1];
    }
    return -.3;
  } };
}

/** Persistent palette/templates; only the map's merged geometry is replaced. */
export class IslandScenery {
  private readonly mapGeometries = new Set<THREE.BufferGeometry>();
  private readonly terrainMaterial: THREE.MeshStandardMaterial;
  private readonly limestoneMaterial: THREE.MeshStandardMaterial;
  private readonly reefMaterial: THREE.MeshStandardMaterial;
  private readonly foliageMaterial: THREE.MeshStandardMaterial;
  private readonly grassMaterial: THREE.MeshStandardMaterial;
  private readonly pathMaterial: THREE.MeshStandardMaterial;
  private readonly rock: THREE.BufferGeometry;
  private readonly shrub: THREE.BufferGeometry;
  private readonly pedestal: THREE.BufferGeometry;
  private readonly tuft: THREE.BufferGeometry;

  constructor(private readonly assets: Assets, private readonly palm: THREE.Group, private readonly lighthouse: THREE.Group, private readonly fortress: THREE.Group) {
    const material = (color: number) => assets.material(new THREE.MeshStandardMaterial({ color, roughness: .96, flatShading: true }));
    this.terrainMaterial = assets.material(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .98 }));
    this.limestoneMaterial = material(0xb6b7a1); this.reefMaterial = material(0x576b61);
    this.foliageMaterial = material(0x23583d); this.grassMaterial = material(0x567b3d); this.grassMaterial.side = THREE.DoubleSide;
    this.pathMaterial = material(0xc9ad73);
    this.rock = assets.geometry(new THREE.DodecahedronGeometry(1, 0));
    this.shrub = assets.geometry(new THREE.IcosahedronGeometry(1, 1));
    this.pedestal = assets.geometry(new THREE.CylinderGeometry(1, 1, 1, 24));
    const positions: number[] = [];
    for (let blade = 0; blade < 5; blade++) {
      const angle = blade * 2.4, x = Math.sin(angle), z = Math.cos(angle);
      positions.push(-z * .16, 0, x * .16, z * .16, 0, -x * .16, x * .35, .65 + blade % 2 * .25, z * .35);
    }
    this.tuft = assets.geometry(new THREE.BufferGeometry()); this.tuft.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3)); this.tuft.computeVertexNormals();
  }

  private mesh(root: THREE.Group, geometry: THREE.BufferGeometry, material: THREE.Material, x: number, y: number, z: number): THREE.Mesh {
    const mesh = new THREE.Mesh(geometry, material); mesh.position.set(x, y, z);
    const noShadow = Boolean(material.userData.landmarkNoShadow);
    mesh.castShadow = !noShadow; mesh.receiveShadow = !noShadow;
    mesh.renderOrder = Number(material.userData.landmarkRenderOrder ?? 0);
    root.add(mesh); return mesh;
  }

  private foundation(root: THREE.Group, ground: Terrain, x: number, z: number, radius: number): number {
    let top = ground.heightAt(x, z), bottom = top;
    for (let i = 0; i < 16; i++) {
      const angle = i / 16 * Math.PI * 2, y = ground.heightAt(x + Math.sin(angle) * radius, z + Math.cos(angle) * radius);
      top = Math.max(top, y); bottom = Math.min(bottom, y);
    }
    top += .12; bottom -= .25;
    this.mesh(root, this.pedestal, this.limestoneMaterial, x, (top + bottom) / 2, z).scale.set(radius, top - bottom, radius);
    return top;
  }

  private path(root: THREE.Group, ground: Terrain, radius: number, fortress: boolean): void {
    const positions: number[] = [], indices: number[] = [], steps = 18;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps, z = radius * (.24 + t * .69), x = Math.sin(t * Math.PI) * radius * .09;
      const width = fortress ? 1.3 : .75;
      for (const side of [-1, 1]) positions.push(x + side * width, ground.heightAt(x + side * width, z) + .035, z);
      if (i < steps) { const a = i * 2; indices.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
    }
    const geometry = this.assets.geometry(new THREE.BufferGeometry()); this.mapGeometries.add(geometry);
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3)); geometry.setIndex(indices); geometry.computeVertexNormals();
    this.mesh(root, geometry, this.pathMaterial, 0, 0, 0);
  }

  rebuild(group: THREE.Group, definitions: IslandDefinition[], isFortress: boolean): void {
    group.clear(); this.mapGeometries.forEach(value => this.assets.removeGeometry(value)); this.mapGeometries.clear();
    definitions.forEach((island, islandIndex) => {
      const seed = islandIndex * .7, ground = terrain(island, seed);
      this.assets.geometry(ground.geometry); this.mapGeometries.add(ground.geometry);
      const root = new THREE.Group(); root.position.set(island.x, 0, island.z); group.add(root);
      this.mesh(root, ground.geometry, this.terrainMaterial, 0, 0, 0);
      const hasFortress = isFortress && islandIndex === 2, hasLighthouse = islandIndex === 0;
      if (hasFortress || hasLighthouse) {
        const building = (hasFortress ? this.fortress : this.lighthouse).clone(true);
        building.position.y = this.foundation(root, ground, 0, 0, hasFortress ? 7.3 : 2.15); root.add(building);
        this.path(root, ground, island.radius, hasFortress);
      }
      // Three small groves leave the beach, path and landmark clear.
      const palms = island.radius >= 25 ? 14 : 10;
      for (let j = 0; j < palms; j++) {
        const cluster = j % 3, angle = seed + .55 + cluster * 2.04 + Math.floor(j / 3) * .17;
        const radius = island.radius * (.42 + j % 4 * .067), x = Math.sin(angle) * radius, z = Math.cos(angle) * radius;
        if (hasFortress && Math.hypot(x, z) < 10) continue;
        const value = this.palm.clone(true); value.scale.setScalar(.83 + j % 4 * .12); value.rotation.y = angle + j;
        value.position.set(x, ground.heightAt(x, z) - .07, z); root.add(value);
      }
      for (let j = 0; j < 32; j++) {
        const angle = j * 2.399 + seed, radius = island.radius * (.34 + j % 6 * .077), x = Math.sin(angle) * radius, z = Math.cos(angle) * radius;
        if ((hasFortress && radius < 10) || (hasLighthouse && radius < 3) || (z > 0 && Math.abs(x) < island.radius * .13)) continue;
        const y = ground.heightAt(x, z), bush = this.mesh(root, this.shrub, this.foliageMaterial, x, y + .4, z);
        bush.scale.set(1 + j % 3 * .3, .65 + j % 2 * .28, .9 + j % 4 * .16); bush.rotation.y = angle;
        const tuft = this.mesh(root, this.tuft, this.grassMaterial, x + .7, ground.heightAt(x + .7, z + .4) - .02, z + .4); tuft.rotation.y = angle; tuft.scale.setScalar(.7 + j % 3 * .2);
      }
      // Weathered limestone outcrops and dark shore rocks, all firmly embedded.
      for (let j = 0; j < 16; j++) {
        const angle = j * 2.14 + seed + 1, radius = island.radius * (.78 + j % 3 * .075), x = Math.sin(angle) * radius, z = Math.cos(angle) * radius;
        const height = j < 4 ? 2.5 + island.height * .12 : .6 + j % 3 * .45;
        const value = this.mesh(root, this.rock, j < 4 ? this.limestoneMaterial : this.reefMaterial, x, ground.heightAt(x, z) + height * .35, z);
        value.scale.set(j < 4 ? 1.5 + j % 2 * .6 : .8 + j % 3 * .5, height, 1.1 + j % 3 * .5); value.rotation.y = angle;
      }
    });
    this.batch(group);
  }

  private batch(group: THREE.Group): void {
    group.updateMatrixWorld(true);
    const buckets = new Map<THREE.Material, { geometries: THREE.BufferGeometry[]; originals: THREE.Mesh[] }>();
    group.traverse(child => {
      if (!(child instanceof THREE.Mesh) || Array.isArray(child.material)) return;
      let bucket = buckets.get(child.material);
      if (!bucket) { bucket = { geometries: [], originals: [] }; buckets.set(child.material, bucket); }
      const clone = child.geometry.clone().applyMatrix4(child.matrixWorld);
      // Shared stone includes both indexed cylinders and non-indexed polyhedra.
      const normalized = clone.index ? clone.toNonIndexed() : clone;
      if (normalized !== clone) clone.dispose();
      bucket.geometries.push(normalized); bucket.originals.push(child);
    });
    buckets.forEach((bucket, material) => {
      const geometry = mergeGeometries(bucket.geometries); bucket.geometries.forEach(value => value.dispose());
      if (!geometry) return;
      this.assets.geometry(geometry); this.mapGeometries.add(geometry);
      bucket.originals.forEach(original => {
        original.parent?.remove(original);
        if (this.mapGeometries.has(original.geometry)) { this.assets.removeGeometry(original.geometry); this.mapGeometries.delete(original.geometry); }
      });
      this.mesh(group, geometry, material, 0, 0, 0);
    });
    // Keep originals if a future incompatible material bucket cannot be merged.
    const pruneEmptyGroups = (root: THREE.Group): void => {
      for (const child of [...root.children]) if (child instanceof THREE.Group) {
        pruneEmptyGroups(child); if (!child.children.length) root.remove(child);
      }
    };
    pruneEmptyGroups(group);
  }
}
